import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Modal,
  Grid,
  Select,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Tooltip,
  Backdrop,
  Fade,
  FormControl,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate } from "react-router-dom";

function getCSRFToken() {
  const name = "csrftoken";
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

const VARIANT_TYPES = ["Color", "Capacidad", "Tamaño", "Presentación", "Material", "Medida", "Combo"];
const IVA_OPTIONS = [0, 5, 15, 999];

const headCellSx = {
  bgcolor: "#1565C0",
  color: "#fff",
  fontWeight: 700,
  whiteSpace: "nowrap",
  py: 1.15,
  fontSize: 14,
};

const actionButtonSx = {
  width: 44,
  height: 38,
  borderRadius: 1.6,
  border: "1px solid #dbeafe",
  bgcolor: "#f8fbff",
  color: "#1565C0",
  "&:hover": {
    bgcolor: "#eef6ff",
  },
};

const deleteButtonSx = {
  width: 44,
  height: 38,
  borderRadius: 1.6,
  border: "1px solid #fecaca",
  bgcolor: "#fff7f7",
  color: "#d32f2f",
  "&:hover": {
    bgcolor: "#ffefef",
  },
};

const modalBoxSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "94%", sm: "88%", md: "82%", lg: "76%" },
  maxHeight: "90vh",
  bgcolor: "#fff",
  borderRadius: 3,
  boxShadow: "0 24px 60px rgba(15,23,42,0.28)",
  overflow: "hidden",
};

export default function AdminVariant() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openListModal, setOpenListModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [currentProductEntry, setCurrentProductEntry] = useState(null);
  const [currentVariant, setCurrentVariant] = useState(null);
  const [variantsInModal, setVariantsInModal] = useState([]);
  const [allowedVariantType, setAllowedVariantType] = useState("");
  const [fields, setFields] = useState({
    variant_type: "",
    value: "",
    price: "",
    stock_minimo: "",
    sku: "",
    image: null,
    iva_porcentaje: 15,
    iva_valor: "",
    stock_mode: "OWN",
    base_variant_id: "",
    conversion_quantity: "1",
  });
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const fmt = (n) => {
    const v = Number(n);
    return Number.isFinite(v) ? v.toFixed(2) : "0.00";
  };

  const normalizeMode = (mode, variant = null) => {
    if (mode === "DERIVED") return "DERIVED_VARIANT";
    if (mode === "DERIVED_PRODUCT" || mode === "DERIVED_VARIANT" || mode === "OWN") return mode;
    const factor = Number(variant?.conversion_quantity || variant?.factor_unidades || 1);
    if (variant?.variant_type === "Presentación" && factor > 1) return "DERIVED_PRODUCT";
    return "OWN";
  };

  const getModeLabel = (mode) => {
    const m = normalizeMode(mode);
    if (m === "DERIVED_PRODUCT") return "Derivada del producto";
    if (m === "DERIVED_VARIANT") return "Derivada de variante";
    return "Stock propio";
  };

  const getModeColor = (mode) => {
    const m = normalizeMode(mode);
    if (m === "DERIVED_PRODUCT") return "info";
    if (m === "DERIVED_VARIANT") return "warning";
    return "success";
  };

  const getConversionQty = (variant) => {
    return variant?.conversion_quantity || variant?.factor_unidades || "1";
  };

  const getBaseLabel = (variant) => {
    const mode = normalizeMode(variant?.stock_mode, variant);

    if (mode === "DERIVED_PRODUCT") {
      return currentProductEntry?.product?.name || variant?.product_name || "Producto base";
    }

    if (mode === "DERIVED_VARIANT") {
      if (variant?.base_variant_name || variant?.base_variant_value) {
        return variant.base_variant_name || variant.base_variant_value;
      }

      const baseId = variant?.base_variant_id || variant?.base_variant;
      const found = variantsInModal.find((v) => String(v.id) === String(baseId));
      return found?.value || "-";
    }

    return "-";
  };

  const calcIva = (price, ivaPorcentaje) => {
    const p = Number(price);
    const iva = Number(ivaPorcentaje);
    if (!Number.isFinite(p)) return "0.00";
    if (iva === 999) return "0.00";
    return ((p * iva) / 100).toFixed(2);
  };

  const loadProductsWithVariants = async () => {
    setLoading(true);
    try {
      const res = await fetch("/productos/ver-productos-con-variantes/", {
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error("No se pudieron cargar los productos con variantes");
      }

      const { products = [] } = await res.json();

      setEntries(
        products.map((p) => ({
          product: p,
          variants: [],
        }))
      );
    } catch (e) {
      setErrorMsg(e.message || "No se pudieron cargar variantes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProductsWithVariants();
  }, []);

  useEffect(() => {
    setFields((prev) => ({
      ...prev,
      iva_valor: calcIva(prev.price, prev.iva_porcentaje),
    }));
  }, [fields.price, fields.iva_porcentaje]);

  const filtered = entries.filter(
    ({ product }) =>
      (product.name || "").toLowerCase().includes((search || "").toLowerCase()) ||
      (product.code || "").toLowerCase().includes((search || "").toLowerCase())
  );

  const openVariants = async (entry) => {
    try {
      const res = await fetch(`/productos/ver-variantes/?code=${encodeURIComponent(entry.product.code)}`, {
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error("No se pudieron cargar las variantes");
      }

      const data = await res.json();
      const variants = Array.isArray(data.variants) ? data.variants : [];
      setCurrentProductEntry({ ...entry, variants });
      setVariantsInModal(variants);
      const firstType = variants.length ? variants[0].variant_type : "";
      setAllowedVariantType(firstType);
      setOpenListModal(true);
    } catch (e) {
      setErrorMsg(e.message || "No se pudieron cargar las variantes");
    }
  };

  const openEdit = (variant) => {
    const mode = normalizeMode(variant.stock_mode, variant);
    const baseId = variant.base_variant_id || variant.base_variant || "";

    setCurrentVariant(variant);
    setFields({
      variant_type: variant.variant_type || "",
      value: variant.value || "",
      price: String(variant.price ?? ""),
      stock_minimo: variant.stock_minimo ?? "",
      sku: variant.sku || "",
      image: variant.image || null,
      iva_porcentaje: variant.iva_porcentaje ?? 15,
      iva_valor: variant.iva_valor ?? "",
      stock_mode: mode,
      base_variant_id: mode === "DERIVED_VARIANT" ? String(baseId || "") : "",
      conversion_quantity: String(getConversionQty(variant) || "1"),
    });
    setOpenEditModal(true);
  };

  const handleField = (e) => {
    const { name, value, files } = e.target;

    if (name === "image") {
      setFields((f) => ({ ...f, image: files && files[0] ? files[0] : null }));
      return;
    }

    if (name === "stock_mode") {
      setFields((f) => ({
        ...f,
        stock_mode: value,
        base_variant_id: value === "DERIVED_VARIANT" ? f.base_variant_id : "",
        conversion_quantity: value === "OWN" ? "1" : f.conversion_quantity || "1",
        stock_minimo: value === "OWN" ? f.stock_minimo : "",
      }));
      return;
    }

    setFields((f) => ({ ...f, [name]: value }));
  };

  const baseVariantOptions = variantsInModal.filter((v) => {
    const mode = normalizeMode(v.stock_mode, v);
    return mode === "OWN" && String(v.id) !== String(currentVariant?.id);
  });

  const saveEdit = async () => {
    try {
      if (allowedVariantType && fields.variant_type && fields.variant_type !== allowedVariantType) {
        setErrorMsg(`Este producto solo permite variantes del tipo "${allowedVariantType}".`);
        return;
      }

      if (!fields.value.trim()) {
        setErrorMsg("El valor de la variante es obligatorio.");
        return;
      }

      if (!fields.price || Number(fields.price) <= 0) {
        setErrorMsg("El precio debe ser mayor a 0.");
        return;
      }

      if (fields.stock_mode === "DERIVED_VARIANT" && !fields.base_variant_id) {
        setErrorMsg("Selecciona la variante base.");
        return;
      }

      if (
        (fields.stock_mode === "DERIVED_PRODUCT" || fields.stock_mode === "DERIVED_VARIANT") &&
        (!fields.conversion_quantity || Number(fields.conversion_quantity) <= 0)
      ) {
        setErrorMsg("La cantidad contenida debe ser mayor a 0.");
        return;
      }

      const csrf = getCSRFToken();
      const body = new FormData();

      body.append("variant_type", fields.variant_type);
      body.append("value", fields.value);
      body.append("price", fields.price);
      body.append("sku", fields.sku);
      body.append("iva_porcentaje", String(fields.iva_porcentaje));
      body.append("stock_mode", fields.stock_mode || "OWN");
      body.append("base_variant_id", fields.stock_mode === "DERIVED_VARIANT" ? fields.base_variant_id || "" : "");
      body.append("conversion_quantity", fields.stock_mode === "OWN" ? "1" : fields.conversion_quantity || "1");

      if (fields.stock_mode === "OWN" && fields.stock_minimo !== "" && fields.stock_minimo !== null && fields.stock_minimo !== undefined) {
        body.append("stock_minimo", String(fields.stock_minimo));
      } else {
        body.append("stock_minimo", "");
      }

      if (fields.image instanceof File) {
        body.append("image", fields.image);
      }

      const res = await fetch(`/productos/actualizar-variante/${currentVariant.id}/`, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrf },
        body,
      });

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Error al actualizar");
      }

      const updatedFromServer = result.variant || {};
      const updated = {
        ...currentVariant,
        ...updatedFromServer,
        variant_type: updatedFromServer.variant_type ?? fields.variant_type,
        value: updatedFromServer.value ?? fields.value,
        price: updatedFromServer.price ?? parseFloat(fields.price || "0"),
        iva_porcentaje: updatedFromServer.iva_porcentaje ?? fields.iva_porcentaje,
        iva_valor: updatedFromServer.iva_valor ?? fields.iva_valor,
        stock_minimo: updatedFromServer.stock_minimo ?? (fields.stock_mode === "OWN" ? fields.stock_minimo : null),
        stock_mode: updatedFromServer.stock_mode ?? fields.stock_mode,
        base_variant_id: updatedFromServer.base_variant_id ?? (fields.stock_mode === "DERIVED_VARIANT" ? fields.base_variant_id : null),
        base_variant: updatedFromServer.base_variant ?? (fields.stock_mode === "DERIVED_VARIANT" ? fields.base_variant_id : null),
        conversion_quantity: updatedFromServer.conversion_quantity ?? (fields.stock_mode === "OWN" ? "1" : fields.conversion_quantity),
        factor_unidades: updatedFromServer.factor_unidades ?? (fields.stock_mode === "OWN" ? "1" : fields.conversion_quantity),
        image: updatedFromServer.image || (fields.image instanceof File ? currentVariant.image : fields.image),
        slug: updatedFromServer.slug || currentVariant.slug,
      };

      setVariantsInModal((vs) => vs.map((v) => (v.id === updated.id ? updated : v)));
      setEntries((es) =>
        es.map((en) =>
          en.product.id === currentProductEntry.product.id
            ? {
                ...en,
                variants: en.variants.map((v) => (v.id === updated.id ? updated : v)),
              }
            : en
        )
      );

      setSuccessMsg("Variante actualizada");
      setOpenEditModal(false);
    } catch (e) {
      setErrorMsg(e.message || "Error al actualizar");
    }
  };

  const deleteVariant = async (id) => {
    try {
      const csrf = getCSRFToken();
      const res = await fetch(`/productos/eliminar-variante/${id}/`, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrf },
      });

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Error al eliminar");
      }

      const newVariants = variantsInModal.filter((v) => v.id !== id);
      setVariantsInModal(newVariants);

      const nextEntries = entries.map((en) =>
        en.product.id === currentProductEntry.product.id
          ? {
              ...en,
              product: {
                ...en.product,
                variant_count: Math.max(Number(en.product.variant_count || 0) - 1, 0),
              },
              variants: en.variants.filter((v) => v.id !== id),
            }
          : en
      );

      setEntries(nextEntries);

      if (newVariants.length === 0) {
        setOpenListModal(false);
      }

      setSuccessMsg("Variante eliminada");
    } catch (e) {
      setErrorMsg(e.message || "Error al eliminar");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                textTransform: "none",
                bgcolor: "#1565C0",
                "&:hover": { bgcolor: "#0D47A1" },
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
              }}
              onClick={() => navigate("nueva")}
            >
              Administrar
            </Button>
          </Box>

          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
            }}
          >
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} mb={2}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 19, color: "#0f172a" }}>
                  Variantes de productos
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.4 }}>
                  Gestiona productos con variantes y revisa su configuración desde el modal
                </Typography>
              </Box>

              <Chip
                label={`${filtered.length} registros`}
                color="info"
                variant="outlined"
                sx={{ fontWeight: 700, borderRadius: 2 }}
              />
            </Stack>

            <TextField
              fullWidth
              placeholder="Buscar producto"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2.2 }}
            />

            <Box
              sx={{
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                overflow: "hidden",
                bgcolor: "#fff",
              }}
            >
              <TableContainer
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  maxWidth: "100%",
                  "&::-webkit-scrollbar": { height: 10 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#cbd5e1",
                    borderRadius: 10,
                  },
                }}
              >
                <Table size="small" sx={{ minWidth: 760 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headCellSx, minWidth: 160 }}>Código</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 360 }}>Producto</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 150 }} align="center">
                        Variantes
                      </TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 170 }} align="center">
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                          <CircularProgress size={26} />
                        </TableCell>
                      </TableRow>
                    ) : filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                          <Stack spacing={0.75} alignItems="center">
                            <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                              No hay variantes para mostrar
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                              Ajusta la búsqueda para encontrar resultados
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((en) => {
                          const p = en.product;
                          const variantsCount = Number(p.variant_count || 0);

                          return (
                            <TableRow
                              key={p.id}
                              hover
                              sx={{
                                "& td": { py: 1.2, borderBottom: "1px solid #edf2f7" },
                                "&:hover": { bgcolor: "#fafcff" },
                              }}
                            >
                              <TableCell sx={{ minWidth: 160 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1565C0" }}>
                                  {p.code || "-"}
                                </Typography>
                              </TableCell>

                              <TableCell sx={{ minWidth: 360 }}>
                                <Typography
                                  sx={{
                                    fontSize: 13.5,
                                    fontWeight: 800,
                                    color: "#0f172a",
                                    textTransform: "uppercase",
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {p.name || "-"}
                                </Typography>
                              </TableCell>

                              <TableCell align="center" sx={{ minWidth: 150 }}>
                                <Chip
                                  label={`${variantsCount} ${variantsCount === 1 ? "variante" : "variantes"}`}
                                  size="small"
                                  variant="filled"
                                  sx={{
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    bgcolor: "#ececec",
                                    "& .MuiChip-label": { px: 1, fontSize: 11.5, color: "#0f172a" },
                                  }}
                                />
                              </TableCell>

                              <TableCell align="center" sx={{ minWidth: 170 }}>
                                <Stack direction="row" spacing={1} justifyContent="center" useFlexGap flexWrap="wrap">
                                  <Tooltip title="Ver variantes" arrow>
                                    <IconButton size="small" onClick={() => openVariants(en)} sx={actionButtonSx}>
                                      <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
                sx={{
                  borderTop: "1px solid #edf2f7",
                  ".MuiTablePagination-toolbar": {
                    px: { xs: 1, sm: 2 },
                  },
                }}
              />
            </Box>
          </Paper>

          <Modal
            open={openListModal}
            onClose={() => setOpenListModal(false)}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 250 }}
          >
            <Fade in={openListModal}>
              <Box sx={modalBoxSx}>
                <Box
                  sx={{
                    px: 2.2,
                    py: 1.6,
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                      Variantes de {currentProductEntry?.product?.name || "-"}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.35 }}>
                      Código: {currentProductEntry?.product?.code || "-"}
                    </Typography>
                  </Box>

                  <Button
                    onClick={() => setOpenListModal(false)}
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Cerrar
                  </Button>
                </Box>

                <Box sx={{ p: 2, overflowY: "auto", maxHeight: "calc(90vh - 72px)" }}>
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={`${variantsInModal.length} ${variantsInModal.length === 1 ? "variante" : "variantes"}`}
                      color="info"
                      variant="outlined"
                      sx={{ fontWeight: 700, borderRadius: 2 }}
                    />
                  </Box>

                  <Box
                    sx={{
                      borderRadius: 2,
                      border: "1px solid #e2e8f0",
                      overflow: "hidden",
                      bgcolor: "#fff",
                    }}
                  >
                    <TableContainer
                      sx={{
                        overflowX: "auto",
                        overflowY: "hidden",
                        maxWidth: "100%",
                        "&::-webkit-scrollbar": { height: 10 },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#cbd5e1",
                          borderRadius: 10,
                        },
                      }}
                    >
                      <Table size="small" sx={{ minWidth: 1380 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ ...headCellSx, minWidth: 120 }}>Imagen</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 150 }}>Tipo</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 180 }}>Valor</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 120 }} align="right">
                              Precio
                            </TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 100 }} align="center">
                              IVA
                            </TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 120 }} align="center">
                              IVA $
                            </TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 190 }} align="center">
                              Inventario
                            </TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 210 }}>Base</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 120 }} align="center">
                              Contenido
                            </TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 130 }} align="center">
                              Stock/Mín
                            </TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 120 }}>SKU</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 160 }} align="center">
                              Acción
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {variantsInModal.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={12} align="center" sx={{ py: 5 }}>
                                <Stack spacing={0.75} alignItems="center">
                                  <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                                    No hay variantes para mostrar
                                  </Typography>
                                  <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                                    Este producto no tiene variantes disponibles
                                  </Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ) : (
                            variantsInModal.map((v) => {
                              const mode = normalizeMode(v.stock_mode, v);
                              const low = v.stock_minimo != null && Number(v.stock ?? 0) <= Number(v.stock_minimo);

                              return (
                                <TableRow
                                  key={v.id}
                                  hover
                                  sx={{
                                    "& td": { py: 1.1, borderBottom: "1px solid #edf2f7" },
                                    "&:hover": { bgcolor: "#fafcff" },
                                  }}
                                >
                                  <TableCell sx={{ minWidth: 120 }}>
                                    {v.image ? (
                                      <Box
                                        sx={{
                                          width: 62,
                                          height: 62,
                                          borderRadius: 2,
                                          border: "1px solid #dbeafe",
                                          bgcolor: "#f8fbff",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <Box
                                          component="img"
                                          src={v.image}
                                          alt=""
                                          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                                        />
                                      </Box>
                                    ) : (
                                      <Typography sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 700 }}>
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 150 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                                      {v.variant_type || "-"}
                                    </Typography>
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 180 }}>
                                    <Typography sx={{ fontSize: 13, color: "#0f172a", fontWeight: 700 }}>
                                      {v.value || "-"}
                                    </Typography>
                                  </TableCell>

                                  <TableCell align="right" sx={{ minWidth: 120 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                                      {fmt(v.price)}
                                    </Typography>
                                  </TableCell>

                                  <TableCell align="center" sx={{ minWidth: 100 }}>
                                    <Chip
                                      label={v.iva_porcentaje === 999 ? "No IVA" : `${v.iva_porcentaje}%`}
                                      size="small"
                                      color={v.iva_porcentaje === 999 ? "default" : "info"}
                                      variant={v.iva_porcentaje === 999 ? "outlined" : "filled"}
                                      sx={{
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        height: 24,
                                        "& .MuiChip-label": { px: 1, fontSize: 11 },
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell align="center" sx={{ minWidth: 120 }}>
                                    <Typography sx={{ fontSize: 13, color: "#0f172a" }}>
                                      {fmt(v.iva_valor)}
                                    </Typography>
                                  </TableCell>

                                  <TableCell align="center" sx={{ minWidth: 190 }}>
                                    <Chip
                                      label={getModeLabel(mode)}
                                      size="small"
                                      color={getModeColor(mode)}
                                      variant={mode === "OWN" ? "outlined" : "filled"}
                                      sx={{
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        height: 24,
                                        "& .MuiChip-label": { px: 1, fontSize: 11 },
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 210 }}>
                                    <Typography sx={{ fontSize: 12.5, color: "#0f172a" }}>
                                      {getBaseLabel(v)}
                                    </Typography>
                                  </TableCell>

                                  <TableCell align="center" sx={{ minWidth: 120 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                                      {mode === "OWN" ? "-" : getConversionQty(v)}
                                    </Typography>
                                  </TableCell>

                                  <TableCell align="center" sx={{ minWidth: 130 }}>
                                    <Chip
                                      label={`${v.stock ?? 0} / ${v.stock_minimo ?? "-"}`}
                                      size="small"
                                      color={low ? "error" : "success"}
                                      variant={low ? "filled" : "outlined"}
                                      sx={{
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        height: 24,
                                        "& .MuiChip-label": { px: 1, fontSize: 11 },
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 120 }}>
                                    <Typography sx={{ fontSize: 12.5, color: "#0f172a" }}>
                                      {v.sku || "-"}
                                    </Typography>
                                  </TableCell>

                                  <TableCell align="center" sx={{ minWidth: 160 }}>
                                    <Stack direction="row" spacing={1} justifyContent="center" useFlexGap flexWrap="wrap">
                                      <Tooltip title="Editar" arrow>
                                        <IconButton size="small" onClick={() => openEdit(v)} sx={actionButtonSx}>
                                          <EditOutlinedIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                      </Tooltip>

                                      <Tooltip title="Eliminar" arrow>
                                        <IconButton size="small" onClick={() => deleteVariant(v.id)} sx={deleteButtonSx}>
                                          <DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Modal>

          <Modal
            open={openEditModal}
            onClose={() => setOpenEditModal(false)}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 250 }}
          >
            <Fade in={openEditModal}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: "94%", sm: "88%", md: 760 },
                  maxHeight: "90vh",
                  bgcolor: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 24px 60px rgba(15,23,42,0.28)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 2.2,
                    py: 1.6,
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                      Editar variante
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.35 }}>
                      {currentProductEntry?.product?.name || "-"}
                    </Typography>
                  </Box>

                  <Button
                    onClick={() => setOpenEditModal(false)}
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Cerrar
                  </Button>
                </Box>

                <Box sx={{ p: 2.5, overflowY: "auto", maxHeight: "calc(90vh - 72px)" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Tipo</InputLabel>
                        <Select name="variant_type" label="Tipo" value={fields.variant_type} onChange={handleField}>
                          {VARIANT_TYPES.map((t) => (
                            <MenuItem key={t} value={t} disabled={!!allowedVariantType && allowedVariantType !== t}>
                              {t}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Valor" name="value" value={fields.value} onChange={handleField} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Precio" name="price" type="number" value={fields.price} onChange={handleField} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>IVA</InputLabel>
                        <Select name="iva_porcentaje" label="IVA" value={fields.iva_porcentaje} onChange={handleField}>
                          {IVA_OPTIONS.map((o) => (
                            <MenuItem key={o} value={o}>
                              {o === 999 ? "No objeto de IVA" : `${o}%`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="IVA $" value={fmt(fields.iva_valor)} InputProps={{ readOnly: true }} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="SKU" name="sku" value={fields.sku} InputProps={{ readOnly: true }} />
                    </Grid>

                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          borderColor: "#bfdbfe",
                          bgcolor: "#f8fbff",
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, color: "#0f172a", mb: 1.5 }}>
                          Configuración de inventario
                        </Typography>

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                              <InputLabel>Manejo de stock</InputLabel>
                              <Select name="stock_mode" label="Manejo de stock" value={fields.stock_mode} onChange={handleField}>
                                <MenuItem value="OWN">Stock propio</MenuItem>
                                <MenuItem value="DERIVED_PRODUCT">Derivada del producto base</MenuItem>
                                <MenuItem value="DERIVED_VARIANT">Derivada de otra variante</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {fields.stock_mode === "OWN" && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Stock mínimo"
                                name="stock_minimo"
                                type="number"
                                value={fields.stock_minimo}
                                onChange={handleField}
                              />
                            </Grid>
                          )}

                          {fields.stock_mode === "DERIVED_PRODUCT" && (
                            <>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  label="Base"
                                  value={currentProductEntry?.product?.name || "Producto actual"}
                                  InputProps={{ readOnly: true }}
                                />
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  label="Cantidad contenida"
                                  name="conversion_quantity"
                                  type="number"
                                  value={fields.conversion_quantity}
                                  onChange={handleField}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                  Esta variante descontará {fields.conversion_quantity || 0} unidad(es) del producto base.
                                </Alert>
                              </Grid>
                            </>
                          )}

                          {fields.stock_mode === "DERIVED_VARIANT" && (
                            <>
                              <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                  <InputLabel>Variante base</InputLabel>
                                  <Select
                                    name="base_variant_id"
                                    label="Variante base"
                                    value={fields.base_variant_id}
                                    onChange={handleField}
                                  >
                                    <MenuItem value="">
                                      <em>Selecciona la base</em>
                                    </MenuItem>
                                    {baseVariantOptions.map((v) => (
                                      <MenuItem key={v.id} value={v.id}>
                                        {v.value} {v.sku ? `- ${v.sku}` : ""}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  label="Cantidad contenida"
                                  name="conversion_quantity"
                                  type="number"
                                  value={fields.conversion_quantity}
                                  onChange={handleField}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <Alert severity={fields.base_variant_id ? "info" : "warning"} sx={{ borderRadius: 2 }}>
                                  {fields.base_variant_id
                                    ? `Esta variante descontará ${fields.conversion_quantity || 0} unidad(es) de la variante base seleccionada.`
                                    : "Selecciona una variante base con stock propio."}
                                </Alert>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Paper>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          width: "100%",
                          height: 150,
                          border: "1px solid #dbeafe",
                          borderRadius: 2,
                          bgcolor: "#f8fbff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {fields.image ? (
                          <Box
                            component="img"
                            src={typeof fields.image === "string" ? fields.image : URL.createObjectURL(fields.image)}
                            alt="preview"
                            sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                          />
                        ) : (
                          <Typography sx={{ color: "#64748b" }}>Sin imagen</Typography>
                        )}
                      </Box>

                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<AddPhotoAlternateOutlinedIcon />}
                        fullWidth
                        sx={{
                          mt: 1.5,
                          textTransform: "none",
                          borderColor: "#1565C0",
                          color: "#1565C0",
                          fontWeight: 700,
                          "&:hover": { borderColor: "#0D47A1", color: "#0D47A1" },
                        }}
                      >
                        Cambiar imagen
                        <input type="file" hidden accept="image/*" name="image" onChange={handleField} />
                      </Button>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ textAlign: "right" }}>
                    <Button
                      variant="contained"
                      onClick={saveEdit}
                      sx={{
                        textTransform: "none",
                        bgcolor: "#1565C0",
                        "&:hover": { bgcolor: "#0D47A1" },
                        borderRadius: 2,
                        px: 2.2,
                        fontWeight: 700,
                        boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
                      }}
                    >
                      Guardar cambios
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Modal>

          <Snackbar
            open={!!successMsg}
            autoHideDuration={3000}
            onClose={() => setSuccessMsg("")}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity="success" onClose={() => setSuccessMsg("")} sx={{ width: "100%" }}>
              {successMsg}
            </Alert>
          </Snackbar>

          <Snackbar
            open={!!errorMsg}
            autoHideDuration={3000}
            onClose={() => setErrorMsg("")}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity="error" onClose={() => setErrorMsg("")} sx={{ width: "100%" }}>
              {errorMsg}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </Box>
  );
}